import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "motion/react";

// Benchmark: a full-width horizontal mouse sweep scrubs through 4.5s of video.
const SCRUB_DURATION_SECONDS = 4.5;
// Smoothing factor bounds (0-1). Lower = silkier easing, higher = snappier 1:1.
// We adapt between these two values based on mouse velocity.
const SCRUB_SMOOTHING_MIN = 0.09; // slow drag → very smooth
const SCRUB_SMOOTHING_MAX = 0.42; // fast whip → snappier follow
// Velocity (px/ms) that maps to SCRUB_SMOOTHING_MAX. Anything faster is clamped.
const SCRUB_VELOCITY_MAX = 2.5;
// Mouse-X thresholds (fraction of viewport width). The title snaps between
// the bottom-left "resting" position and the top-right position.
// Asymmetric thresholds: moving right past ENTER snaps to top-right;
// moving left back past EXIT snaps to bottom-left. EXIT > ENTER means the
// right-side title disappears earlier on the return sweep.
const TITLE_SNAP_ENTER = 0.45;
const TITLE_SNAP_EXIT = 0.78;

const VIDEO_SRC =
  "https://public.readdy.ai/ai/video_res/019eb4a9-68e4-7482-af82-6e8a87cc3c7a.mp4";

export default function HeroSection() {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const prevMouseXRef = useRef<number | null>(null);
  const titleAtTopRef = useRef(false);
  const [titleAtTop, setTitleAtTop] = useState(false);

  // Preload the entire clip as a blob URL so scrubbing reads from local
  // memory instead of streaming chunks over a slow connection.
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    let cancelled = false;
    let objectUrl: string | null = null;

    const fullyPreload = async () => {
      try {
        const res = await fetch(VIDEO_SRC, { cache: "force-cache" });
        if (!res.ok) return;
        const blob = await res.blob();
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        // Preserve current playback position across the src swap.
        const prevTime = video.currentTime;
        video.src = objectUrl;
        video.load();
        const restore = () => {
          try {
            video.currentTime = prevTime;
          } catch {
            /* ignore */
          }
          video.removeEventListener("loadedmetadata", restore);
        };
        video.addEventListener("loadedmetadata", restore);
      } catch {
        /* network failed — fall back to streamed <source> */
      }
    };

    fullyPreload();

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, []);

  // Hook 1: Desktop mouse X-axis dragging video timeline ("Mouse Scrubbing")
  // Trigger: window width >= 1024px. Skipped on mobile.
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    if (typeof window === "undefined") return;
    if (window.innerWidth < 1024) return;

    video.loop = false;
    video.autoplay = false;
    video.muted = true;
    video.pause();

    let targetTime = video.currentTime || 0;
    let smoothedTime = video.currentTime || 0;
    let isSeeking = false;
    let lastMoveAt = 0;
    let lastMoveX = 0;
    let mouseVelocity = 0; // px/ms, EMA-smoothed
    let lastPreciseSeekAt = 0;
    let rafId = 0;
    let rafRunning = false;
    let isHeroVisible = true;

    const handleSeeked = () => {
      isSeeking = false;
    };

    const stopLoop = () => {
      rafRunning = false;
      if (rafId) cancelAnimationFrame(rafId);
      rafId = 0;
    };

    const currentSmoothing = () => {
      const t = Math.min(1, mouseVelocity / SCRUB_VELOCITY_MAX);
      // ease-out so middle speeds already feel responsive
      const eased = 1 - Math.pow(1 - t, 2);
      return (
        SCRUB_SMOOTHING_MIN +
        (SCRUB_SMOOTHING_MAX - SCRUB_SMOOTHING_MIN) * eased
      );
    };

    const tick = () => {
      if (!isHeroVisible) {
        stopLoop();
        return;
      }

      if (video.readyState >= 2) {
        // Velocity decays toward zero when the mouse stops moving.
        const now = performance.now();
        if (now - lastMoveAt > 30) {
          mouseVelocity *= 0.85;
        }

        // Adaptive easing: snappy on whips, silky on slow drags.
        const k = currentSmoothing();
        smoothedTime += (targetTime - smoothedTime) * k;

        const diff = smoothedTime - video.currentTime;
        const movingRecently = now - lastMoveAt < 120;

        if (!isSeeking && Math.abs(diff) > 0.005) {
          isSeeking = true;
          if (movingRecently && typeof video.fastSeek === "function") {
            try {
              video.fastSeek(smoothedTime);
            } catch {
              video.currentTime = smoothedTime;
            }
          } else {
            video.currentTime = smoothedTime;
            lastPreciseSeekAt = now;
          }
        } else if (
          !isSeeking &&
          !movingRecently &&
          now - lastPreciseSeekAt > 120 &&
          Math.abs(targetTime - video.currentTime) > 0.0005
        ) {
          isSeeking = true;
          smoothedTime = targetTime;
          video.currentTime = targetTime;
          lastPreciseSeekAt = now;
        } else if (
          !isSeeking &&
          !movingRecently &&
          Math.abs(targetTime - smoothedTime) <= 0.0005 &&
          Math.abs(targetTime - video.currentTime) <= 0.0005
        ) {
          stopLoop();
          return;
        }
      }
      rafId = requestAnimationFrame(tick);
    };

    const ensureLoop = () => {
      if (rafRunning || !isHeroVisible) return;
      rafRunning = true;
      rafId = requestAnimationFrame(tick);
    };

    const handleMouseMove = (e: MouseEvent) => {
      const now = performance.now();
      const currentX = e.clientX;

      // Direction-aware title snap (bottom-left ⇄ top-right)
      const ratio = currentX / window.innerWidth;
      let shouldBeTop = titleAtTopRef.current;
      if (!titleAtTopRef.current && ratio > TITLE_SNAP_ENTER) {
        shouldBeTop = true;
      } else if (titleAtTopRef.current && ratio < TITLE_SNAP_EXIT) {
        shouldBeTop = false;
      }
      if (shouldBeTop !== titleAtTopRef.current) {
        titleAtTopRef.current = shouldBeTop;
        setTitleAtTop(shouldBeTop);
      }

      if (prevMouseXRef.current === null) {
        prevMouseXRef.current = currentX;
        lastMoveAt = now;
        lastMoveX = currentX;
        ensureLoop();
        return;
      }

      const delta = currentX - prevMouseXRef.current;
      prevMouseXRef.current = currentX;

      // Update velocity (EMA over instant px/ms)
      const dt = Math.max(1, now - lastMoveAt);
      const instantV = Math.abs(currentX - lastMoveX) / dt;
      mouseVelocity = mouseVelocity * 0.6 + instantV * 0.4;
      lastMoveAt = now;
      lastMoveX = currentX;

      const duration = video.duration;
      if (!duration || Number.isNaN(duration)) return;

      targetTime += (delta / window.innerWidth) * SCRUB_DURATION_SECONDS;
      if (targetTime < 0) targetTime = 0;
      if (targetTime > duration) targetTime = duration;

      ensureLoop();
    };

    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          isHeroVisible = entry.isIntersecting;
          if (!isHeroVisible) {
            stopLoop();
          }
        }
      },
      { threshold: 0 },
    );
    io.observe(video);

    video.addEventListener("seeked", handleSeeked);
    window.addEventListener("mousemove", handleMouseMove, { passive: true });

    return () => {
      io.disconnect();
      video.removeEventListener("seeked", handleSeeked);
      window.removeEventListener("mousemove", handleMouseMove);
      stopLoop();
    };
  }, []);

  // Hook 2: Mobile autoplay (< 1024px) — force autoplay, loop, no mouse scrubbing.
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const setupMobile = () => {
      if (window.innerWidth < 1024) {
        video.autoplay = true;
        video.loop = true;
        video.muted = true;
        const playPromise = video.play();
        if (playPromise && typeof playPromise.catch === "function") {
          playPromise.catch(() => {
            /* ignore */
          });
        }
      }
    };

    setupMobile();
    window.addEventListener("resize", setupMobile);

    return () => {
      window.removeEventListener("resize", setupMobile);
    };
  }, []);

  return (
    <>
      {/* Background Video */}
      <div className="order-last lg:order-none relative lg:absolute lg:inset-0 lg:z-0 overflow-hidden pointer-events-none w-full aspect-square md:aspect-video lg:aspect-auto lg:h-full bg-neutral-50 lg:bg-transparent">
        <video
          muted
          playsInline
          preload="auto"
          disablePictureInPicture
          ref={videoRef}
          className="w-full h-full object-cover object-center"
        >
          <source
            src="https://public.readdy.ai/ai/video_res/019eb4a9-68e4-7482-af82-6e8a87cc3c7a.mp4"
            type="video/mp4"
          />
        </video>
      </div>

      {/* Content — editorial bottom-corner composition */}
      <div className="relative z-10 flex flex-col order-first lg:order-none w-full bg-white lg:bg-transparent pb-10 lg:pb-0 lg:min-h-screen">
        <main
          id="spade-hero"
          className="relative w-full mx-auto px-6 lg:px-10 pt-12 lg:pt-0 flex-1 flex flex-col lg:justify-end lg:pb-10"
        >
          {/* Title — shakes out on exit, bounces in on enter when threshold is crossed */}
          <AnimatePresence mode="wait" initial={false}>
            <motion.h1
              key={titleAtTop ? "top-right" : "bottom-left"}
              initial={{ opacity: 1 }}
              animate={{ opacity: 1 }}
              exit={{
                opacity: 0,
                x: [0, -10, 10, -8, 8, -5, 5, 0],
                transition: { duration: 0.18, ease: "easeInOut" },
              }}
              transition={{ duration: 0 }}
              className={`font-sans font-extrabold uppercase tracking-[-0.02em] text-[#1C2E1E] leading-[0.88] text-5xl sm:text-6xl md:text-7xl lg:text-[6.5rem] xl:text-[8rem] whitespace-pre-line lg:absolute ${
                titleAtTop
                  ? "lg:top-44 lg:right-10 lg:text-right"
                  : "lg:bottom-10 lg:left-10 lg:text-left"
              }`}
            >
              {"we'd love\nto hear"}
            </motion.h1>
          </AnimatePresence>

          {/* Bottom-right: Justified uppercase caption */}
          <div className="flex justify-end mt-10 lg:mt-0">
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="font-sans uppercase tracking-[0.08em] text-[11px] md:text-[12px] leading-[1.7] text-[#1C2E1E] max-w-xs lg:max-w-[260px] text-left lg:text-justify"
            >
              A studio for the curious and the considered — drop us a message
              about collaborations, openings, or anything else on your mind, and
              we'll get back to you shortly.
            </motion.p>
          </div>
        </main>
      </div>
    </>
  );
}